import React, { useState, useEffect } from 'react';
import Modal from '../components/Modal';
import { categoryService } from '../services/categoryService';
import { Category, CategoryFormData } from '../types/types';
import { Tag, Plus, Edit2, Trash2, Search } from 'lucide-react';
import './CategoryManagement.css';

const CategoryManagement: React.FC = () => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [formData, setFormData] = useState<CategoryFormData>({
        name: '',
        description: '',
    });

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        setLoading(true);
        try {
            const data = await categoryService.getAllCategories();
            setCategories(data);
        } catch (error) {
            console.error('Error loading categories:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setFormData({
            name: '',
            description: '',
        });
        setCreateModalOpen(true);
    };

    const handleEdit = (category: Category) => {
        setSelectedCategory(category);
        setFormData({
            name: category.name,
            description: category.description || '',
        });
        setEditModalOpen(true);
    };

    const handleDelete = (category: Category) => {
        setSelectedCategory(category);
        setDeleteModalOpen(true);
    };

    const confirmCreate = async () => {
        try {
            console.log("Prueba Categoria: ", formData);
            await categoryService.createCategory(formData);
            await loadCategories();
            setCreateModalOpen(false);
        } catch (error) {
            console.error('Error creating category:', error);
            alert('Error al crear la categoría');
        }
    };

    const confirmEdit = async () => {
        if (!selectedCategory) return;

        try {
            await categoryService.updateCategory(selectedCategory.id, formData);
            await loadCategories();
            setEditModalOpen(false);
            setSelectedCategory(null);
        } catch (error) {
            console.error('Error updating category:', error);
            alert('Error al actualizar la categoría');
        }
    };

    const confirmDelete = async () => {
        if (!selectedCategory) return;

        try {
            await categoryService.deleteCategory(selectedCategory.id);
            await loadCategories();
            setDeleteModalOpen(false);
            setSelectedCategory(null);
        } catch (error) {
            console.error('Error deleting category:', error);
            alert('Error al eliminar la categoría');
        }
    };

    const filteredCategories = categories.filter(
        (category) =>
            category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            category.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="category-management-container">
                <div className="page-header">
                    <div>
                        <h1>Gestión de Categorías</h1>
                        <p className="page-subtitle">Administrar categorías de productos</p>
                    </div>
                    <Tag size={32} className="header-icon" />
                </div>

                <div className="card">
                    <div className="table-controls">
                        <div className="search-box">
                            <Search size={20} />
                            <input
                                type="text"
                                placeholder="Buscar categorías..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <button className="btn btn-primary" onClick={handleCreate}>
                            <Plus size={20} />
                            Nueva Categoría
                        </button>
                    </div>

                    {loading ? (
                        <div className="loading-state">Cargando categorías...</div>
                    ) : (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Nombre</th>
                                        <th>Descripción</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCategories.map((category) => (
                                        <tr key={category.id}>
                                            <td>{category.id}</td>
                                            <td>
                                                <div className="category-cell">
                                                    <span className="category-name">{category.name}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="category-description">
                                                    {category.description || 'Sin descripción'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        className="btn-icon btn-edit"
                                                        onClick={() => handleEdit(category)}
                                                        title="Editar categoría"
                                                    >
                                                        <Edit2 size={18} />
                                                    </button>
                                                    <button
                                                        className="btn-icon btn-delete"
                                                        onClick={() => handleDelete(category)}
                                                        title="Eliminar categoría"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredCategories.length === 0 && (
                                <div className="empty-state">No se encontraron categorías</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Create Modal */}
                <Modal
                    isOpen={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                    title="Crear Nueva Categoría"
                    size="medium"
                >
                    <CategoryForm
                        formData={formData}
                        setFormData={setFormData}
                        onSubmit={confirmCreate}
                        onCancel={() => setCreateModalOpen(false)}
                    />
                </Modal>

                {/* Edit Modal */}
                <Modal
                    isOpen={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    title="Editar Categoría"
                    size="medium"
                >
                    <CategoryForm
                        formData={formData}
                        setFormData={setFormData}
                        onSubmit={confirmEdit}
                        onCancel={() => setEditModalOpen(false)}
                    />
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    title="Confirmar Eliminación"
                    size="small"
                >
                    <div className="delete-confirmation">
                        <p>
                            ¿Está seguro que desea eliminar la categoría{' '}
                            <strong>{selectedCategory?.name}</strong>?
                        </p>
                        <p className="warning-text">Esta acción no se puede deshacer.</p>

                        <div className="modal-actions">
                            <button className="btn btn-outline" onClick={() => setDeleteModalOpen(false)}>
                                Cancelar
                            </button>
                            <button className="btn btn-danger" onClick={confirmDelete}>
                                Eliminar
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
    );
};

// Category Form Component
interface CategoryFormProps {
    formData: CategoryFormData;
    setFormData: React.Dispatch<React.SetStateAction<CategoryFormData>>;
    onSubmit: () => void;
    onCancel: () => void;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
    formData,
    setFormData,
    onSubmit,
    onCancel,
}) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    return (
        <div className="modal-form">
            <div className="form-group">
                <label htmlFor="name">Nombre de la Categoría *</label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                />
            </div>

            <div className="form-group">
                <label htmlFor="description">Descripción</label>
                <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="form-input"
                />
            </div>

            <div className="modal-actions">
                <button className="btn btn-outline" onClick={onCancel}>
                    Cancelar
                </button>
                <button className="btn btn-primary" onClick={onSubmit}>
                    Guardar
                </button>
            </div>
        </div>
    );
};

export default CategoryManagement;
